/**
 * PATH: apps/qa/src/components/SortableAreaItem.tsx
 * PURPOSE: Sortable area item + area list for the inspection structure drawer.
 * WHY: Extracted from InspectionStructureDrawer.tsx to keep files under 300 lines.
 */

import { useState, useRef, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { areaApi, checklistApi } from '@/lib/api'
import type { FloorItem, AreaItem } from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  GripVertical, Pencil, Trash2, Check, XCircle, Loader2,
} from 'lucide-react'

function SortableAreaItem({
  area,
  inspectionId,
  onScrollTo,
  onApplyTemplate,
}: {
  area: AreaItem
  inspectionId: string
  onScrollTo: (type: 'floor' | 'area', id: string) => void
  onApplyTemplate?: (areaIds: string[]) => void
}) {
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(area.area_name)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const editRef = useRef<HTMLInputElement>(null)

  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: area.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  }

  const renameMutation = useMutation({
    mutationFn: (name: string) => areaApi.updateArea(area.id, { area_name: name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-floors', inspectionId] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => areaApi.deleteArea(area.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-floors', inspectionId] })
      queryClient.invalidateQueries({ queryKey: ['qa-inspection', inspectionId] })
    },
  })

  const { data: checklistData } = useQuery({
    queryKey: ['qa-checklists', area.id],
    queryFn: () => checklistApi.getInstances({ area_id: area.id }),
    staleTime: 60_000,
  })

  const checklists = Array.isArray(checklistData?.results) ? checklistData.results : []
  const templateName = checklists.length > 0 ? (checklists[0] as Record<string, unknown>).template_name as string : null
  const itemCount = checklists.reduce((sum, cl) => sum + (Array.isArray((cl as Record<string, unknown>).items) ? ((cl as Record<string, unknown>).items as unknown[]).length : 0), 0)

  const isLocked = area.status === 'locked'

  useEffect(() => { if (isEditing) editRef.current?.focus() }, [isEditing])

  return (
    <div ref={setNodeRef} style={style} className={cn('bg-background', isDragging && 'shadow-md')}>
      <div className="flex items-center gap-1 px-3 py-1.5 group">
        <button {...attributes} {...listeners} className="p-0.5 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground" title="Drag to reorder">
          <GripVertical className="w-3 h-3" />
        </button>

        <span className={cn(
          'w-1.5 h-1.5 rounded-full flex-shrink-0',
          isLocked ? 'bg-emerald-500' :
          area.status === 'in_progress' ? 'bg-blue-500' :
          area.status === 'blocked' ? 'bg-amber-500' :
          'bg-muted-foreground/30'
        )} />

        {isEditing ? (
          <form className="flex-1 flex items-center gap-1"
            onSubmit={(e) => { e.preventDefault(); if (editName.trim()) renameMutation.mutate(editName.trim()) }}>
            <input ref={editRef} value={editName} onChange={(e) => setEditName(e.target.value)}
              className="flex-1 text-xs px-1.5 py-0.5 rounded border border-input bg-background text-foreground focus:ring-1 focus:ring-primary"
              onKeyDown={(e) => { if (e.key === 'Escape') { setIsEditing(false); setEditName(area.area_name) } }} />
            <button type="submit" disabled={renameMutation.isPending} className="p-0.5 text-emerald-600">
              {renameMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            </button>
            <button type="button" onClick={() => { setIsEditing(false); setEditName(area.area_name) }} className="p-0.5 text-muted-foreground">
              <XCircle className="w-3 h-3" />
            </button>
          </form>
        ) : (
          <>
            <button onClick={() => onScrollTo('area', area.id)} className="flex-1 text-left min-w-0">
              <span className={cn(
                'text-xs text-foreground hover:text-primary transition-colors truncate block',
                isLocked && 'line-through text-muted-foreground'
              )}>
                {area.area_name}
              </span>
              {templateName && (
                <span className="text-[10px] text-muted-foreground truncate block">
                  {templateName} · {itemCount} items
                </span>
              )}
              {!templateName && (
                <button
                  onClick={(e) => { e.stopPropagation(); onApplyTemplate?.([area.id]) }}
                  className="text-[10px] text-primary/70 hover:text-primary">
                  + Apply template
                </button>
              )}
            </button>

            {area.area_code && (
              <span className="text-[9px] text-muted-foreground/60 font-mono flex-shrink-0">{area.area_code}</span>
            )}

            <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => { setIsEditing(true); setEditName(area.area_name) }} className="p-0.5 text-muted-foreground hover:text-foreground" title="Rename">
                <Pencil className="w-2.5 h-2.5" />
              </button>
              <button onClick={() => setDeleteConfirm(true)} className="p-0.5 text-muted-foreground hover:text-destructive" title="Delete">
                <Trash2 className="w-2.5 h-2.5" />
              </button>
            </div>
          </>
        )}
      </div>

      {deleteConfirm && (
        <div className="px-3 py-1.5 bg-destructive/5 border-t border-destructive/10">
          <p className="text-[10px] text-destructive mb-1">Delete "{area.area_name}" and its checklist data?</p>
          <div className="flex items-center gap-2">
            <button onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}
              className="px-2 py-0.5 text-[10px] font-medium rounded bg-destructive text-destructive-foreground">
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
            <button onClick={() => setDeleteConfirm(false)} className="px-2 py-0.5 text-[10px] rounded border border-input hover:bg-accent">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function AreaList({
  floor,
  areas,
  inspectionId,
  onScrollTo,
  onApplyTemplate,
}: {
  floor: FloorItem
  areas: AreaItem[]
  inspectionId: string
  onScrollTo: (type: 'floor' | 'area', id: string) => void
  onApplyTemplate?: (areaIds: string[]) => void
}) {
  const queryClient = useQueryClient()
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const reorderMutation = useMutation({
    mutationFn: (order: { id: string; sort_order: number }[]) => areaApi.reorder(order),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['qa-floors', inspectionId] }),
  })

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = areas.findIndex(a => a.id === active.id)
    const newIndex = areas.findIndex(a => a.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    const reordered = [...areas]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)
    reorderMutation.mutate(reordered.map((a, i) => ({ id: a.id, sort_order: i })))
  }

  const areaIds = useMemo(() => areas.map(a => a.id), [areas])

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={areaIds} strategy={verticalListSortingStrategy}>
        <div className="divide-y divide-border/30">
          {areas.map(area => (
            <SortableAreaItem key={area.id} area={area} inspectionId={inspectionId}
              onScrollTo={onScrollTo} onApplyTemplate={onApplyTemplate} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}
